import React from 'react';
import { Code, Settings, Cloud, Award, GraduationCap, BookOpen } from 'lucide-react';
import { skills, certifications, education } from '../data/mock';
import { Badge } from './ui/badge';
import useScrollAnimation from '../hooks/useScrollAnimation';

const Skills = () => {
  const [headerRef, headerVisible] = useScrollAnimation(0.2);
  const [skillsRef, skillsVisible] = useScrollAnimation(0.1);
  const [eduRef, eduVisible] = useScrollAnimation(0.1);

  const skillCategories = [
    {
      title: 'Leadership & Strategy',
      icon: Settings,
      skills: skills.leadership,
      color: 'teal'
    },
    {
      title: 'Technical Expertise',
      icon: Code,
      skills: skills.technical,
      color: 'slate'
    },
    {
      title: 'Platforms & Tools',
      icon: Cloud,
      skills: skills.platforms,
      color: 'teal'
    }
  ];

  return (
    <section id="skills" className="py-24 bg-slate-950">
      <div className="max-w-7xl mx-auto px-6">
        <div 
          ref={headerRef}
          className={`text-center mb-16 animate-on-scroll ${headerVisible ? 'visible' : ''}`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal-500/10 border border-teal-500/20 mb-6">
            <Code className="w-4 h-4 text-teal-400" />
            <span className="text-teal-400 text-sm font-medium">Expertise</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Skills & Qualifications
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            A comprehensive toolkit for modern IT leadership in educational settings
          </p>
        </div>

        {/* Skills Grid */}
        <div ref={skillsRef} className="grid md:grid-cols-3 gap-8 mb-16">
          {skillCategories.map((category, index) => (
            <div
              key={index}
              className={`p-6 rounded-2xl bg-slate-900 border border-slate-800 hover:border-teal-500/30 transition-all duration-300 animate-scale-in stagger-${index + 1} ${skillsVisible ? 'visible' : ''}`}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-teal-500/10">
                  <category.icon className="w-5 h-5 text-teal-400" />
                </div>
                <h3 className="text-lg font-semibold text-white">{category.title}</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {category.skills.map((skill, sIndex) => (
                  <Badge
                    key={sIndex}
                    variant="secondary"
                    className="bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700 px-3 py-1.5"
                  >
                    {skill}
                  </Badge>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Education & Certifications */}
        <div ref={eduRef} className="grid md:grid-cols-2 gap-8">
          {/* Education */}
          <div className={`p-6 md:p-8 rounded-2xl bg-slate-900 border border-slate-800 animate-slide-left ${eduVisible ? 'visible' : ''}`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-xl bg-teal-500/10">
                <GraduationCap className="w-5 h-5 text-teal-400" />
              </div>
              <h3 className="text-xl font-semibold text-white">Education</h3>
            </div>
            <div className="space-y-6">
              {education.map((edu, index) => (
                <div key={index} className="relative pl-6 border-l-2 border-slate-700">
                  <div className="absolute left-0 top-0 w-2 h-2 rounded-full bg-teal-500 -translate-x-[5px]" />
                  <h4 className="text-white font-medium">{edu.degree}</h4>
                  {edu.focus && <p className="text-teal-400 text-sm">{edu.focus}</p>}
                  <p className="text-slate-400 text-sm">{edu.institution}</p>
                  <p className="text-slate-500 text-sm">{edu.location} • {edu.year}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Certifications */}
          <div className={`p-6 md:p-8 rounded-2xl bg-slate-900 border border-slate-800 animate-slide-right stagger-2 ${eduVisible ? 'visible' : ''}`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-xl bg-teal-500/10">
                <Award className="w-5 h-5 text-teal-400" />
              </div>
              <h3 className="text-xl font-semibold text-white">Certifications & Programs</h3>
            </div>
            <div className="space-y-4">
              {certifications.map((cert, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 p-4 rounded-xl bg-slate-950 border border-slate-800 hover:border-teal-500/30 transition-colors"
                >
                  <div className="p-2 rounded-lg bg-teal-500/10">
                    <BookOpen className="w-5 h-5 text-teal-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">{cert.name}</h4>
                    <p className="text-slate-500 text-sm">{cert.issuer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Skills;
